import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Mail, Phone, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const productOptions = [
  "Girls' Wear",
  "Boys' Wear",
  "Baby Wear",
  "Ladieswear",
  "Denim Products",
  "Embroidered Items",
  "Multiple Categories",
];

const ContactSection = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    country: '',
    product_interest: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Inquiry submitted successfully! We will get back to you soon.');
      setFormData({
        company_name: '',
        contact_person: '',
        email: '',
        phone: '',
        country: '',
        product_interest: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-slate-50"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left - Info */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-sans text-sm font-medium tracking-[0.3em] uppercase text-slate-400 mb-4">
              Get in Touch
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-slate-900 mb-6">
              Partner With<br />
              <span className="text-rose-700">Strawberry Studio</span>
            </h2>
            <p className="font-sans text-lg text-slate-600 mb-10">
              Ready to explore manufacturing partnership? Get in touch with us for inquiries 
              about our products, capabilities, or to schedule a factory visit.
            </p>

            {/* Contact Cards */}
            <div className="space-y-6">
              {/* Noida Office */}
              <div className="bg-white p-6 border border-slate-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-rose-700 flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-slate-900 mb-1">
                      Wovens Facility - Noida
                    </h3>
                    <p className="font-sans text-sm text-slate-600">
                      Strawberry Studio Exports Pvt. Ltd.<br />
                      Noida, Uttar Pradesh, India<br />
                      (Near New Delhi)
                    </p>
                  </div>
                </div>
              </div>

              {/* Bangalore Office */}
              <div className="bg-white p-6 border border-slate-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-rose-700 flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-slate-900 mb-1">
                      Knits Facility - Bangalore
                    </h3>
                    <p className="font-sans text-sm text-slate-600">
                      Maestro Fashions (Sister Concern)<br />
                      Bangalore, Karnataka, India
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white p-6 border border-slate-100">
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-rose-700" strokeWidth={1.5} />
                    <span className="font-sans text-sm text-slate-600">monika@strawberry-studio.com</span>
                  </div>
                </div>
                <div className="bg-white p-6 border border-slate-100">
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-rose-700" strokeWidth={1.5} />
                    <span className="font-sans text-sm text-slate-600">+91-9810529070</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          >
            {isSubmitted ? (
              <div className="bg-white p-12 border border-slate-100 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={32} className="text-green-600" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl text-slate-900 mb-4">
                  Thank You!
                </h3>
                <p className="font-sans text-slate-600 mb-6">
                  Your inquiry has been submitted successfully. Our team will review your 
                  requirements and get back to you within 24-48 hours.
                </p>
                <Button
                  onClick={() => setIsSubmitted(false)}
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-6"
                >
                  Submit Another Inquiry
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 border border-slate-100">
                <h3 className="font-serif text-2xl text-slate-900 mb-8">
                  Business Inquiry Form
                </h3>

                <div className="space-y-6">
                  {/* Company Name */}
                  <div>
                    <label className="block font-sans text-sm font-medium text-slate-700 mb-2">
                      Company Name *
                    </label>
                    <Input
                      data-testid="input-company"
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      required
                      className="h-14 bg-slate-50 border-transparent focus:border-slate-900 focus:bg-white transition-all duration-300 rounded-none px-6 text-base"
                      placeholder="Your company name"
                    />
                  </div>

                  {/* Two Column Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-sans text-sm font-medium text-slate-700 mb-2">
                        Contact Person *
                      </label>
                      <Input
                        data-testid="input-contact"
                        type="text"
                        name="contact_person"
                        value={formData.contact_person}
                        onChange={handleChange}
                        required
                        className="h-14 bg-slate-50 border-transparent focus:border-slate-900 focus:bg-white transition-all duration-300 rounded-none px-6 text-base"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block font-sans text-sm font-medium text-slate-700 mb-2">
                        Country *
                      </label>
                      <Input
                        data-testid="input-country"
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        className="h-14 bg-slate-50 border-transparent focus:border-slate-900 focus:bg-white transition-all duration-300 rounded-none px-6 text-base"
                        placeholder="Your country"
                      />
                    </div>
                  </div>

                  {/* Two Column Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-sans text-sm font-medium text-slate-700 mb-2">
                        Email *
                      </label>
                      <Input
                        data-testid="input-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="h-14 bg-slate-50 border-transparent focus:border-slate-900 focus:bg-white transition-all duration-300 rounded-none px-6 text-base"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="block font-sans text-sm font-medium text-slate-700 mb-2">
                        Phone
                      </label>
                      <Input
                        data-testid="input-phone"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="h-14 bg-slate-50 border-transparent focus:border-slate-900 focus:bg-white transition-all duration-300 rounded-none px-6 text-base"
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                  </div>

                  {/* Product Interest */}
                  <div>
                    <label className="block font-sans text-sm font-medium text-slate-700 mb-2">
                      Product Interest *
                    </label>
                    <select
                      data-testid="select-product"
                      name="product_interest"
                      value={formData.product_interest}
                      onChange={handleChange}
                      required
                      className="w-full h-14 bg-slate-50 border-transparent focus:border-slate-900 focus:bg-white transition-all duration-300 rounded-none px-6 text-base appearance-none"
                    >
                      <option value="">Select product category</option>
                      {productOptions.map((option, index) => (
                        <option key={index} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block font-sans text-sm font-medium text-slate-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      data-testid="input-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="bg-slate-50 border-transparent focus:border-slate-900 focus:bg-white transition-all duration-300 rounded-none px-6 py-4 text-base resize-none"
                      placeholder="Tell us about your requirements, quantities, and timeline..."
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    data-testid="submit-inquiry-btn"
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Send size={18} strokeWidth={1.5} />
                        Submit Inquiry
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
